import { type Map as ImmutableMap } from "immutable";

import { type SqlString } from "../SqlString";
import { type SqliteTableDef } from "../schema";
import { type SqliteTableInfo } from "./runGetTableXInfo";

export type ColumnDiff = {
	name: string;
	type: string;
	nullable: boolean;
	primaryKey: boolean;
	unique: boolean;
	defaultValue: string | number | boolean | SqlString | null | undefined;
};

export type TableDiff = {
	addedColumns: ColumnDiff[];
	removedColumns: ColumnDiff[];
	modifiedColumns: {
		name: string;
		current: ColumnDiff;
		desired: ColumnDiff;
	}[];
};

export type SchemaDiff = ImmutableMap<string, TableDiff>;

/**
 * Computes the differences between the current and desired table schemas
 */
export function computeSchemaDiff(
	currentTable: SqliteTableInfo,
	desiredTable: SqliteTableDef
): TableDiff {
	const addedColumns: ColumnDiff[] = [];
	const removedColumns: ColumnDiff[] = [];
	const modifiedColumns: { name: string; current: ColumnDiff; desired: ColumnDiff }[] = [];

	// Convert current table columns to ColumnDiff format
	const currentColumns = new Map<string, ColumnDiff>(
		currentTable.columns.map((col) => [
			col.name,
			{
				name: col.name,
				type: col.type,
				nullable: col.notnull === 0,
				primaryKey: col.pk === 1,
				unique: false, // SQLite doesn't expose unique constraints in table_info
				defaultValue: col.dflt_value,
			} as ColumnDiff,
		])
	);

	// Convert desired table columns to ColumnDiff format
	const desiredColumns = new Map<string, ColumnDiff>(
		desiredTable.columns.map((col) => [
			col.name,
			{
				name: col.name,
				type: col.type,
				nullable: col.nullable !== false,
				primaryKey: col.primaryKey === true,
				unique: col.unique === true,
				defaultValue: col.defaultValue,
			} as ColumnDiff,
		])
	);

	// Find added and modified columns
	for (const [name, desiredCol] of desiredColumns.entries()) {
		const currentCol = currentColumns.get(name);
		if (!currentCol) {
			addedColumns.push(desiredCol);
		} else if (!areColumnsEqual(currentCol, desiredCol)) {
			modifiedColumns.push({
				name,
				current: currentCol,
				desired: desiredCol,
			});
		}
	}

	// Find removed columns
	for (const [name, currentCol] of currentColumns.entries()) {
		if (!desiredColumns.has(name)) {
			removedColumns.push(currentCol);
		}
	}

	return {
		addedColumns,
		removedColumns,
		modifiedColumns,
	};
}

function areColumnsEqual(current: ColumnDiff, desired: ColumnDiff): boolean {
	// Compare type
	const currentUndefined = current.type === "undefined" || current.type === undefined;
	const desiredUndefined = desired.type === "undefined" || desired.type === undefined;
	// If both are undefined, they are equal.
	if (current.type !== desired.type && !currentUndefined && !desiredUndefined) {
		return false;
	}

	// Compare nullable
	if (current.nullable !== desired.nullable) {
		return false;
	}

	// Compare primary key
	if (current.primaryKey !== desired.primaryKey) {
		return false;
	}

	// Compare unique
	if (current.unique !== desired.unique) {
		return false;
	}

	// Compare default value
	// Handle SQLite's CURRENT_TIMESTAMP special case
	if (
		current.defaultValue === "CURRENT_TIMESTAMP" &&
		desired.defaultValue === "CURRENT_TIMESTAMP"
	) {
		return true;
	}

	// Handle null/undefined cases
	if (current.defaultValue === null && desired.defaultValue === undefined) {
		return true;
	}
	if (current.defaultValue === undefined && desired.defaultValue === null) {
		return true;
	}

	// Compare other default values
	if (current.defaultValue !== desired.defaultValue) {
		return false;
	}

	return true;
}
